/** 確認の間隔の下限。タブの切り替えが続いても version.json を取りに行きすぎないようにする。 */
const MIN_CHECK_INTERVAL_MS = 60_000;
/** 画面を開いたまま使い続けている間の確認の間隔。 */
const PERIODIC_CHECK_INTERVAL_MS = 5 * 60_000;
/**
 * 読み込み直したあと、この間は読み込み直さない。デプロイの途中で古い画面が返ったときや、
 * チャンクが本当に無いときに読み込み直しを繰り返さないため。過ぎれば次の確認で再び試す。
 */
const RELOAD_GUARD_MS = 60_000;
const RELOADED_AT_KEY = "izanagi:app-version-reloaded-at";

/** 配信中の版の ID。version.json が無い配信 (開発サーバーなど) では null。 */
export async function fetchDeployedBuildId(): Promise<string | null> {
	const res = await fetch("/version.json", { cache: "no-cache" });
	// 無い配信では SPA のフォールバックで index.html が返る。比べる相手が無いだけなので失敗にしない。
	if (!res.ok || !res.headers.get("Content-Type")?.includes("application/json")) return null;
	const body: unknown = await res.json();
	const buildId =
		typeof body === "object" && body !== null ? (body as { buildId?: unknown }).buildId : null;
	return typeof buildId === "string" ? buildId : null;
}

/**
 * 直前に読み込み直していなければ読み込み直し、true を返す。読み込み直した時刻を記録できない
 * (sessionStorage が使えない) ときは、繰り返しを防げないので読み込み直さない。
 */
export function reloadUnlessRecentlyReloaded(now: number, reload: () => void): boolean {
	const reloadedAt = Number(readSession(RELOADED_AT_KEY) ?? "0");
	if (now - reloadedAt < RELOAD_GUARD_MS) return false;
	if (!writeSession(RELOADED_AT_KEY, String(now))) return false;
	reload();
	return true;
}

/**
 * タブに戻ったときと、表示中は一定時間ごとに配信中の版を確かめ、このタブの版と違えば読み込み直す。
 * デプロイで古いチャンクが消えて読み込みに失敗したときも、画面が壊れたままになるので読み込み直す。
 */
export function installAppVersionWatch(): void {
	const reload = () => window.location.reload();
	let lastCheckedAt = Number.NEGATIVE_INFINITY;
	const checkIfVisible = () => {
		const now = Date.now();
		if (document.visibilityState !== "visible" || now - lastCheckedAt < MIN_CHECK_INTERVAL_MS)
			return;
		lastCheckedAt = now;
		fetchDeployedBuildId()
			.then((buildId) => {
				if (buildId !== null && buildId !== __BUILD_ID__)
					reloadUnlessRecentlyReloaded(Date.now(), reload);
			})
			// 結果を待たない契機なので、失敗はログに残して次の確認に任せる。
			.catch((err: unknown) => console.error("配信中の版の確認に失敗しました", err));
	};
	document.addEventListener("visibilitychange", checkIfVisible);
	window.setInterval(checkIfVisible, PERIODIC_CHECK_INTERVAL_MS);
	window.addEventListener("vite:preloadError", (event) => {
		if (reloadUnlessRecentlyReloaded(Date.now(), reload)) event.preventDefault();
	});
}

/** sessionStorage が使えない環境 (ストレージ無効など) では null。書き込みも失敗するので読み込み直しはしない。 */
function readSession(key: string): string | null {
	try {
		return sessionStorage.getItem(key);
	} catch (err) {
		console.error("sessionStorage を読めませんでした", err);
		return null;
	}
}

/** 書き込めたら true。 */
function writeSession(key: string, value: string): boolean {
	try {
		sessionStorage.setItem(key, value);
		return true;
	} catch (err) {
		console.error("sessionStorage に書けませんでした", err);
		return false;
	}
}
